import {
  Alert,
  AppBar,
  Badge,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { useAuth } from '../../../hooks/useAppContext';
import { useNavigate } from 'react-router-dom';
import {
  KeyboardArrowDown,
  LockOutlined,
  Logout,
  MarkEmailUnreadOutlined,
  Menu as MenuIcon,
  NotificationsNone,
  PersonOutline,
  WarningAmberOutlined,
} from '@mui/icons-material';
import logo from '../../../assets/SVG/logo.png';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProfileData } from '../../../redux/reducers/profileSlice';
import { fetchProjectProfileData } from '../../../redux/reducers/projectProfileSlice';
import socket from '../../../socket';
import { SocketResponse } from '../../../types';
import dayjs from 'dayjs';
import axios from 'axios';
import { toast } from 'react-toastify';
import ChangePasswordModal from '../../ChangePassword';
interface HeaderProps {
  drawerToggler: () => void;
}
interface ChangePasswordValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}
type NotificationItem = {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
};
const Header: React.FC<HeaderProps> = ({ drawerToggler }) => {
  const { logout, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const profile = useSelector((state: any) => state.profileSlice.profile)
  const loading = useSelector((state: any) => state.profileSlice.loading);
  const [isAlert, setIsAlert] = useState(false)
  const [Expirationdays, setExpirationdays] = useState<number>(0)
  const [hasAlertBeenShown, setHasAlertBeenShown] = useState<boolean>(false);
  const projectProfile = useSelector((state: any) => state.projectProfileSlice.projectProfile)
  const dispatch = useDispatch()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [openResetPasswordModal, setOpenResetPasswordModal] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] = useState(false);
  const menuOpen = Boolean(menuAnchor);
  const notificationMenuOpen = Boolean(notificationAnchor);
  const isEmailVerified = profile?.data?.isEmailVerified === true;
  useEffect(() => {
    dispatch(fetchProfileData() as any)
    dispatch(fetchProjectProfileData() as any);
  }, [dispatch])
  useEffect(() => {
    localStorage.setItem('projectOP', profile?.data?.projectId);
  }, [loading])


  // Check if the alert has been shown before on initial load
  useEffect(() => {
    const alertStatus = localStorage.getItem('expirationAlertShown');
    if (alertStatus === 'true') {
      setHasAlertBeenShown(true);
    }
  }, []);
  useEffect(() => {
    if (!profile?.data?.projectId) return;

    const projectId = profile.data.projectId.toString();

    const handleProfileUpdate = (data: SocketResponse) => {

      if (data.module === 'profile' && data.operation === 'update') {
        dispatch(fetchProfileData() as any);
        dispatch(fetchProjectProfileData() as any);
      }
    };


    socket.on(projectId, handleProfileUpdate);

    // Cleanup on unmount or when project ID changes
    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off(projectId, handleProfileUpdate);
    };
  }, [profile?.data?.projectId, dispatch]);
  const [imageError, setImageError] = useState<boolean>(false);

  const handleImageError = () => {
    setImageError(true);
  };
  const closeMenu = () => setMenuAnchor(null);
  const closeNotificationMenu = () => setNotificationAnchor(null);
  const handleLogout = () => {
    closeMenu();
    if (isLoggedIn) {
      localStorage.removeItem("expirationAlertShown")
      logout();
      navigate('/');
    }
  };
  const handlePasswordChange = (values: ChangePasswordValues) => {
    let data = JSON.stringify({
      oldPassword: values?.oldPassword,
      newPassword: values?.newPassword,
    });

    let config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: `${process.env.REACT_APP_BASE_URL}/auth/changePassword`,
      headers: {
        accept: '*/*',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json',
      },
      data: data,
    };

    axios
      .request(config)
      .then((response) => {
        if (response?.data?.isSuccess) {
          toast.success(response?.data?.message);
        } else {
          toast.error(response?.data?.message);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };
  const handleResendVerificationLink = () => {
    const email = profile?.data?.email;
    if (!email) {
      toast.error('Email address not found in your profile.');
      return;
    }
    if (resendVerificationLoading) return;

    setResendVerificationLoading(true);
    axios
      .request({
        method: 'post',
        maxBodyLength: Infinity,
        url: `${process.env.REACT_APP_ADMIN_API_URL}/auth/resendEmailVarificationLink`,
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
        },
        data: { email },
      })
      .then((response) => {
        if (response?.data?.isSuccess) {
          toast.success(
            response?.data?.message
              ? `${response.data.message} to ${email}`
              : `Verification link sent to ${email}`
          );
          closeNotificationMenu();
        } else {
          toast.error(response?.data?.message || 'Failed to resend verification link.');
        }
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || 'Failed to resend verification link.');
      })
      .finally(() => {
        setResendVerificationLoading(false);
      });
  };
  const isAdmin = Boolean(
    profile?.data?.userType?.includes('admin') ||
      profile?.data?.userType?.includes('superAdmin'),
  );
  const displayName = isAdmin
    ? [profile?.data?.firstName, profile?.data?.lastName].filter(Boolean).join(' ').trim()
    : (profile?.data?.fullName || '').trim();
  const profileReady = !loading && Boolean(profile?.data) && Boolean(displayName);
  const welcomeName = profileReady ? `Welcome, ${displayName}` : '';
  const contractDaysRemaining = useMemo(() => {
    if (!projectProfile?.contractExpiryDate) return null;
    return dayjs(projectProfile.contractExpiryDate).diff(dayjs(), 'day');
  }, [projectProfile?.contractExpiryDate]);
  // Email verification prompt is for admin accounts only (not doctor/staff users).
  const showEmailVerificationNotification =
    profileReady && isAdmin && !isEmailVerified;
  const showContractExpiryNotification =
    contractDaysRemaining !== null && contractDaysRemaining <= 20;
  const contractExpiryDescription = useMemo(() => {
    const contactHelp =
      ' For renewal assistance, contact us at info.pakhims@gmail.com or 923001201336.';
    if (contractDaysRemaining === null) return '';
    if (contractDaysRemaining < 0) {
      return `Your subscription expired ${Math.abs(contractDaysRemaining)} day${Math.abs(contractDaysRemaining) === 1 ? '' : 's'} ago. Please renew to avoid service interruption.${contactHelp}`;
    }
    if (contractDaysRemaining === 0) {
      return `Your subscription expires today. Please renew to avoid service interruption.${contactHelp}`;
    }
    return `Your subscription will expire in ${contractDaysRemaining} day${contractDaysRemaining === 1 ? '' : 's'}. Please renew in advance.${contactHelp}`;
  }, [contractDaysRemaining]);
  const notifications = useMemo<NotificationItem[]>(() => {
    const items: NotificationItem[] = [];

    if (showEmailVerificationNotification) {
      items.push({
        id: 'email-verification',
        title: 'Please Verify your account to get more updates',
        description: resendVerificationLoading
          ? 'Sending verification link...'
          : 'Click to resend the email verification link.',
        icon: resendVerificationLoading ? (
          <CircularProgress size={18} />
        ) : (
          <MarkEmailUnreadOutlined fontSize="small" color="warning" />
        ),
        onClick: handleResendVerificationLink,
        disabled: resendVerificationLoading,
      });
    }

    if (showContractExpiryNotification) {
      items.push({
        id: 'contract-expiry',
        title: 'Contract expiry',
        description: contractExpiryDescription,
        icon: <WarningAmberOutlined fontSize="small" color="warning" />,
      });
    }

    // Future notification items can be pushed here.
    return items;
  }, [
    showEmailVerificationNotification,
    showContractExpiryNotification,
    contractExpiryDescription,
    resendVerificationLoading,
  ]);
  const notificationCount = notifications.length;
  const checkDateAlert = (providedDate: string) => {
    const currentDate = dayjs();
    const providedDateTime = dayjs(providedDate);
    const differenceInDays = providedDateTime.diff(currentDate, 'day');
    setExpirationdays(differenceInDays)
    if (differenceInDays <= 20 && differenceInDays >= 1) {
      setIsAlert(true);
    } else {
      setIsAlert(false);
    }
  };
  const handleCloseAlert = () => {
    setIsAlert(false);
    localStorage.setItem('expirationAlertShown', 'false'); // Set flag to indicate the alert has been shown
  };
  useEffect(() => {
    if (hasAlertBeenShown) {
      checkDateAlert(projectProfile?.contractExpiryDate)
    }

  }, [projectProfile])

  return (

    <Box sx={{ flexGrow: 1 }}>
      {isAlert && Expirationdays !== null && (
        <Box sx={{ width: "100vw", position: "fixed", display: "flex", justifyContent: "center", zIndex: "2", top: "0", left: "0", right: "0" }}>
        
          <Alert severity="warning" onClose={handleCloseAlert}>
          Your subscription will expire in{" "}
            <span style={{ fontWeight: "bold" }}>
              {Math.abs(Expirationdays || 0)} day{Expirationdays > 1 ? "s" : ""}
            </span>
            . Please renew in advance to avoid service interruption. For renewal assistance,
            contact us at{" "}
            <span style={{ fontWeight: "bold" }}>info.pakhims@gmail.com</span> or{" "}
            <span style={{ fontWeight: "bold" }}>923001201336</span>.
          </Alert>

        </Box>
      )}
      <AppBar position='static'>
        <Toolbar>
          <IconButton
            size='large'
            edge='start'
            color='inherit'
            aria-label='open drawer'
            sx={{ mr: 2 }}
            onClick={() => drawerToggler()}
          >
            <MenuIcon />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              margin: 'auto',
            }}
          >
            <Typography
              variant='h6'
              noWrap
              component='div'
              sx={{ flexGrow: 1, display: { xs: 'none', sm: 'none', md: 'block' } }}
            >

              {projectProfile?.hospitalLogoUrl ? <img src={projectProfile?.hospitalLogoUrl} alt={projectProfile?.hospitalName + ' logo'} style={{ height: '40px', cursor: 'pointer' }} onClick={() => navigate('/')} onError={handleImageError} /> : <img src={logo} style={{ height: '40px', cursor: 'pointer' }} onClick={() => navigate('/')} />}

            </Typography>
            <Typography
              variant='h6'
              noWrap
              component='div'
              sx={{ flexGrow: 1, display: { xs: 'none', lg: 'block', sm: 'none' }, textTransform: 'uppercase' }}
            >
              {projectProfile?.hospitalName}
            </Typography>
            {isLoggedIn && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {profileReady && (
                  <Typography variant='h6' sx={{ fontSize: { lg: '1.25rem', md: '1rem', xs: '0.8rem' }, textTransform: 'capitalize' }}>
                    {welcomeName}
                  </Typography>
                )}
                {profileReady && (
                  <>
                    <IconButton
                      color="inherit"
                      onClick={(e) => setNotificationAnchor(e.currentTarget)}
                      aria-controls={notificationMenuOpen ? 'notification-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={notificationMenuOpen ? 'true' : undefined}
                      aria-label="Open notifications"
                      sx={{
                        border: '1px solid rgba(255,255,255,0.35)',
                        borderRadius: '10px',
                        ml: 1,
                      }}
                    >
                      <Badge
                        badgeContent={notificationCount || undefined}
                        color="error"
                        overlap="circular"
                        sx={{
                          '& .MuiBadge-badge': {
                            fontWeight: 700,
                          },
                        }}
                      >
                        <NotificationsNone />
                      </Badge>
                    </IconButton>
                    <Menu
                      id="notification-menu"
                      anchorEl={notificationAnchor}
                      open={notificationMenuOpen}
                      onClose={closeNotificationMenu}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{
                        sx: {
                          mt: 1,
                          width: 340,
                          maxWidth: '90vw',
                          borderRadius: '12px',
                          boxShadow: '0 12px 30px rgba(15, 55, 110, 0.16)',
                        },
                      }}
                    >
                      <Box sx={{ px: 2, py: 1.25 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e' }}>
                          Notifications
                        </Typography>
                      </Box>
                      <Divider />
                      {notifications.length === 0 ? (
                        <MenuItem disabled sx={{ py: 2, opacity: 1 }}>
                          <ListItemText
                            primary="No new notifications"
                            primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                          />
                        </MenuItem>
                      ) : (
                        notifications.map((item, index) => (
                          <Box key={item.id}>
                            <MenuItem
                              onClick={() => {
                                if (item.disabled || !item.onClick) return;
                                item.onClick();
                              }}
                              disabled={item.disabled}
                              sx={{
                                alignItems: 'flex-start',
                                py: 1.5,
                                whiteSpace: 'normal',
                                cursor: item.onClick ? 'pointer' : 'default',
                                '&:hover': item.onClick
                                  ? undefined
                                  : { backgroundColor: 'transparent' },
                              }}
                            >
                              <ListItemIcon sx={{ mt: 0.25, minWidth: 36 }}>
                                {item.icon}
                              </ListItemIcon>
                              <ListItemText
                                primary={item.title}
                                secondary={item.description}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: 600,
                                  color: '#1a237e',
                                }}
                                secondaryTypographyProps={{
                                  variant: 'caption',
                                  color: 'text.secondary',
                                  sx: { whiteSpace: 'normal' },
                                }}
                              />
                            </MenuItem>
                            {index < notifications.length - 1 && <Divider />}
                          </Box>
                        ))
                      )}
                    </Menu>
                  </>
                )}
                <IconButton
                  color="inherit"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  aria-controls={menuOpen ? 'account-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={menuOpen ? 'true' : undefined}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.35)',
                    borderRadius: '10px',
                    ml: 1,
                    px: 0.6,
                  }}
                >
                  <PersonOutline />
                  <KeyboardArrowDown fontSize="small" />
                </IconButton>
                <Menu
                  id="account-menu"
                  anchorEl={menuAnchor}
                  open={menuOpen}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      borderRadius: '12px',
                      boxShadow: '0 12px 30px rgba(15, 55, 110, 0.16)',
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      navigate('/');
                    }}
                  >
                    <ListItemIcon>
                      <PersonOutline fontSize="small" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      closeMenu();
                      setOpenResetPasswordModal(true);
                    }}
                  >
                    <ListItemIcon>
                      <LockOutlined fontSize="small" />
                    </ListItemIcon>
                    Change Password
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <ChangePasswordModal
        open={openResetPasswordModal}
        onClose={() => setOpenResetPasswordModal(false)}
        handleSubmit={handlePasswordChange}
      />
    </Box>
  );
};
export default Header;
